import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { ui } from "@patient-watch/ui";
import { SELECT_FIELD_LABEL_CLASSNAME } from "@patient-watch/ui/select";
import type { MultiSelectAddOptionActions } from "@patient-watch/ui/multi-select";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import type { OptionT } from "./Select";
import { FormFieldLabelWithAccessory } from "./components/formFieldLabelRow";
import { FormFieldLockedControl } from "./components/formFieldLockedControl";
import type { LooseRHFField } from "./utils/loose-rhf-field";

function optionLabelToString(label: ReactNode): string {
    if (label === null || label === undefined) return "";
    if (typeof label === "string") return label;
    if (typeof label === "number" || typeof label === "boolean") return String(label);
    return "";
}

interface Props {
    name: string;
    label?: string;
    description?: ReactNode;
    trigger: React.ReactNode;
    options: OptionT[];
    className?: string;
    popoverContentClassName?: string;
    disabled?: boolean;
    addOption?: ReactNode | ((actions: MultiSelectAddOptionActions) => ReactNode);
    addOptionTitle?: string;
    addOptionTriggerLabel?: string;
}

const MultiSelect = ({
    name,
    trigger,
    options,
    label,
    description,
    className,
    popoverContentClassName,
    disabled,
    addOption,
    addOptionTitle,
    addOptionTriggerLabel,
}: Props) => {
    const form = useFormContext();
    const safeOptions = options ?? [];

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }: { field: LooseRHFField }) => {
                const selectedValues = (field.value as string[] | undefined) ?? [];

                return (
                    <FormItem className="w-full">
                        {label ? (
                            <FormFieldLabelWithAccessory
                                label={<FormLabel className={SELECT_FIELD_LABEL_CLASSNAME}>{label}</FormLabel>}
                            />
                        ) : null}
                        {description ? <FormDescription>{description}</FormDescription> : null}
                        <FormFieldLockedControl>
                            <FormControl>
                                <ui.MultiSelect
                                    options={safeOptions.map((o) => ({
                                        label: optionLabelToString(o.label) || o.searchText || o.value,
                                        value: o.value,
                                        description: o.description,
                                        searchText: o.searchText,
                                        icon: o.icon,
                                        group: o.group,
                                        disabled: o.locked,
                                    }))}
                                    value={selectedValues}
                                    onValueChange={field.onChange}
                                    placeholder={typeof trigger === "string" ? trigger : "Select options"}
                                    className={className}
                                    popoverContentClassName={popoverContentClassName}
                                    disabled={disabled}
                                    addOption={addOption}
                                    addOptionTitle={addOptionTitle}
                                    addOptionTriggerLabel={addOptionTriggerLabel}
                                />
                            </FormControl>
                        </FormFieldLockedControl>
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
};

export default MultiSelect;
